import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Instagram, Youtube, Facebook, ExternalLink, Calendar, Music, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import musikonzeptLogo from "@/assets/musikonzept-logo.png";
import { INFO_TYP, infoAdresse, kommendeLesen } from "@/lib/termine/infoveranstaltungen";

interface TerminLink {
  id: string;
  titel: string;
  slug: string;
  typ: string;
  datum: string;
  bild_url: string | null;
  /** Sitzung 14b: Infoveranstaltungen führen auf ihre Landingpage statt auf /termin/<slug> */
  adresse?: string;
}

const STATIC_LINKS = [
  { label: "📚 Alle Kurse entdecken", href: "/kurse" },
  { label: "💰 Preise & Abo-Optionen", href: "/preise" },
  { label: "📰 Kostenloser Newsletter", href: "/newsletter" },
  { label: "📞 Kontakt", href: "/kontakt" },
];

const Links = () => {
  const [termine, setTermine] = useState<TerminLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTermine = async () => {
      try {
        const now = new Date().toISOString();
        const [{ data }, infos] = await Promise.all([
          supabase
            .from("termine")
            .select("id, titel, slug, typ, datum, bild_url")
            .in("typ", ["Themenwebinare", "Kostenlose Webinare"])
            .gte("datum", now)
            .eq("status", "live")
            .order("datum", { ascending: true })
            .limit(8),
          // Kommende Infoveranstaltungen (Sitzung 14b), nach Datum dazwischen; ein Fehler kostet nur diese Karten
          supabase.rpc("marketing_info_kommende", { p_grenze: 3 }),
        ]);
        const infoLinks: TerminLink[] = kommendeLesen(infos.error ? [] : infos.data).map((i) => ({
          id: `info-${i.termin_id}`,
          titel: i.titel,
          slug: i.slug,
          typ: INFO_TYP,
          datum: i.beginn,
          bild_url: i.bild_url,
          adresse: infoAdresse(i, "links"),
        }));
        setTermine(
          [...(data || []), ...infoLinks]
            .sort((a, b) => new Date(a.datum).getTime() - new Date(b.datum).getTime())
            .slice(0, 8),
        );
      } catch (err) {
        console.error("Fehler beim Laden der Termine:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTermine();
  }, []);

  const formatDatum = (datum: string) =>
    format(new Date(datum), "dd. MMMM yyyy · HH:mm 'Uhr'", { locale: de });

  const typLabel = (typ: string) =>
    typ === INFO_TYP ? INFO_TYP : typ === "Themenwebinare" ? "Themen-Webinar" : "Kreativ-Webinar";

  const typColor = (typ: string) =>
    typ === "Themenwebinare"
      ? "from-[hsl(var(--primary))] to-[hsl(var(--accent))]"
      : "from-orange-400 to-amber-500";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--primary)/0.08)] via-background to-[hsl(var(--accent)/0.06)] flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">

        {/* Branding */}
        <div className="text-center space-y-3">
          <img
            src={musikonzeptLogo}
            alt="MusiKonzept"
            className="h-16 w-auto mx-auto"
          />
          <p className="text-sm text-muted-foreground">
            @musikonzept · Ideen für mehr Musik mit Kindern
          </p>
        </div>

        {/* Fixed top link: Kostenlos registrieren */}
        <a
          href="/kostenlos"
          className="w-full block rounded-2xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] p-[2px] shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="rounded-[14px] bg-card/95 backdrop-blur-sm px-5 py-4 flex items-center gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] flex items-center justify-center text-lg">
              🎁
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground text-base leading-tight">
                Kostenlos registrieren
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Starter-Zugang & Praxisinhalte · Jetzt sichern
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-primary shrink-0" />
          </div>
        </a>

        {/* Fixed top link: Fachkraft für Musik */}
        <a
          href="/ausbildung/fachkraft-musik"
          className="w-full block rounded-2xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] p-[2px] shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="rounded-[14px] bg-card/95 backdrop-blur-sm px-5 py-4 flex items-center gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground text-base leading-tight">
                Fachkraft für Musik
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Ausbildung für den Elementarbereich · Jetzt informieren
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-primary shrink-0" />
          </div>
        </a>

        {/* Fixed top link: Fachkraft für Musik und Sprache */}
        <a
          href="/ausbildung/fachkraft-musik-und-sprache"
          className="w-full block rounded-2xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] p-[2px] shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="rounded-[14px] bg-card/95 backdrop-blur-sm px-5 py-4 flex items-center gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground text-base leading-tight">
                Fachkraft für Musik und Sprache
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Ausbildung für Musik & Sprachbildung · Jetzt informieren
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-primary shrink-0" />
          </div>
        </a>

        {/* Dynamic termine */}
        {loading ? (
          <div className="py-6 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : termine.length > 0 ? (
          <div className="w-full space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider px-1">
              Nächste Termine
            </h2>
            {termine.map((t) => (
              <a
                key={t.id}
                href={t.adresse ?? `/termin/${t.slug}`}
                className="w-full block rounded-xl border border-border/60 bg-card/90 backdrop-blur-sm px-4 py-3.5 hover:shadow-md hover:border-primary/40 transition-all hover:scale-[1.01] active:scale-[0.99]"
              >
                <div className="flex items-center gap-3">
                  {t.bild_url ? (
                    <img src={t.bild_url} alt={t.titel} className="shrink-0 w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className={`shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${typColor(t.typ)} flex items-center justify-center`}>
                      <Music className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm leading-tight truncate">
                      {t.titel}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Calendar className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {formatDatum(t.datum)}
                      </span>
                    </div>
                  </div>
                </div>
                <span className={`inline-block mt-2 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gradient-to-r ${typColor(t.typ)} text-white`}>
                  {typLabel(t.typ)}
                </span>
              </a>
            ))}
          </div>
        ) : null}

        {/* Static links */}
        <div className="w-full space-y-2.5">
          {STATIC_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="w-full block rounded-xl border border-border/60 bg-card/90 backdrop-blur-sm px-5 py-3.5 text-center font-medium text-foreground hover:shadow-md hover:border-primary/40 transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Social icons */}
        <div className="flex items-center gap-5 pt-2 pb-4">
          <a
            href="https://www.instagram.com/musikonzept/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            aria-label="Instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <a
            href="https://www.youtube.com/@MusiKonzept"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            aria-label="YouTube"
          >
            <Youtube className="w-5 h-5" />
          </a>
          <a
            href="https://www.facebook.com/musikonzept"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-card border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            aria-label="Facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
        </div>

        <p className="text-[11px] text-muted-foreground/60 pb-4">
          © {new Date().getFullYear()} MusiKonzept
        </p>
      </div>
    </div>
  );
};

export default Links;
